import { useEffect, useMemo, useRef } from 'react'
import { applyTheme, readTheme, saveTheme } from '../session/theme'
import rawHtml from './home.html?raw'
import './home.css'

/**
 * De publieke landingspagina op `/`.
 *
 * De markup is één samenhangend geheel (hero, mockups, prijzen, FAQ, contact)
 * en leeft daarom als los HTML-bestand dat hier rauw wordt ingeladen, in plaats
 * van als honderden regels JSX met evenveel losse SVG's. De stijl staat in
 * `home.css`, gescoped onder `.home` zodat de generieke selectors de rest van
 * de app niet raken. De kleuren komen uit de app-tokens, dus licht/donker loopt
 * vanzelf mee.
 *
 * Alles wat de pagina doet (de galerij-tegels tekenen, de confetti, de
 * scrollspy, het mobiele menu, het contactformulier) staat in één effect dat
 * zich tot deze pagina beperkt en bij het verlaten weer opruimt.
 */

declare global {
  interface Window {
    __CONFETTI__?: { publicHost?: string }
  }
}

/**
 * Het publieke domein, bij runtime uit de container-env.
 *
 * De API serveert `/config.js` met `window.__CONFETTI__.publicHost`, afgeleid
 * van `PublicUrl:BaseUrl` — dezelfde waarde die al per omgeving gezet wordt voor
 * de QR-codes. Zo promoveert dezelfde build van staging naar prod zonder de
 * frontend opnieuw te bouwen: alleen de env verschilt. Lokaal (Vite, geen API)
 * bestaat dat bestand niet en valt het terug op de testhost.
 *
 * De inlog- en registreerknoppen gebruiken dit bewust níet — die zijn relatief
 * (`/beheer/...`) en werken dus sowieso op elk domein. Dit stuurt alleen wat er
 * als tekst te zien is: de URL in de mockup en het mailadres.
 */
const PUBLIC_HOST = window.__CONFETTI__?.publicHost || 'confetti.flync.be'

export function HomePage() {
  const ref = useRef<HTMLDivElement>(null)
  const html = useMemo(() => rawHtml.replaceAll('__HOST__', PUBLIC_HOST), [])

  useEffect(() => {
    const root = ref.current
    if (!root) {
      return
    }

    const cleanups: Array<() => void> = []
    const $ = (sel: string) => root.querySelector<HTMLElement>(sel)

    // --- Thema-knop, gekoppeld aan de app-brede voorkeur -------------------
    const toggle = $('#theme-toggle')
    const sun = root.querySelector<SVGElement>('[data-sun]')
    const moon = root.querySelector<SVGElement>('[data-moon]')
    const systemDark = () => window.matchMedia('(prefers-color-scheme: dark)').matches
    const isDark = () => {
      const t = readTheme()
      return t === 'dark' || (t === 'system' && systemDark())
    }
    const paint = () => {
      const dark = isDark()
      if (sun) sun.toggleAttribute('hidden', dark)
      if (moon) moon.toggleAttribute('hidden', !dark)
    }
    paint()

    if (toggle) {
      const onToggle = () => {
        saveTheme(isDark() ? 'light' : 'dark')
        paint()
      }
      toggle.addEventListener('click', onToggle)
      cleanups.push(() => toggle.removeEventListener('click', onToggle))
    }
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onMedia = () => {
      if (readTheme() === 'system') {
        applyTheme('system')
        paint()
      }
    }
    media.addEventListener('change', onMedia)
    cleanups.push(() => media.removeEventListener('change', onMedia))

    // --- Galerij-tegels: warme verlopen als "foto's" ----------------------
    const grads = [
      ['#c98a5e', '#8a4a2d'],
      ['#8aa0b0', '#4f6377'],
      ['#b58a9c', '#6f4f63'],
      ['#d9b56a', '#9c7a2e'],
      ['#b98a86', '#7d514c'],
      ['#92a583', '#5c6f4e'],
      ['#caa06a', '#8f6a3c'],
      ['#a98fa0', '#6f5468'],
      ['#7f9aa8', '#4d6673'],
      ['#d7a97f', '#a86a3f'],
      ['#9fae8c', '#6a7d55'],
      ['#c58f7a', '#8a5240'],
    ]
    const names = [
      'Tante Marie',
      'Opa Wim',
      'Jasper & Nele',
      'Sofie',
      'Els',
      'Thomas',
      'Oma Rit',
      'Lies',
      'Ben',
      'Fien',
      'Wout',
      'Noor',
    ]
    const grad = (i: number) => {
      const p = grads[i % grads.length]
      return `linear-gradient(140deg,${p[0]},${p[1]})`
    }
    const fillTiles = (
      id: string,
      count: number,
      opts: { names?: boolean; video?: boolean; offset?: number } = {},
    ) => {
      const el = $('#' + id)
      if (!el) return
      el.textContent = ''
      const frag = document.createDocumentFragment()
      for (let i = 0; i < count; i++) {
        const off = opts.offset ?? 0
        const t = document.createElement('div')
        t.className = 'tile' + (opts.video && i % 7 === 3 ? ' vid' : '')
        t.style.background = grad(i + off)
        if (opts.names) {
          const w = document.createElement('span')
          w.className = 'who'
          w.textContent = names[(i + off) % names.length]
          t.appendChild(w)
        }
        frag.appendChild(t)
      }
      el.appendChild(frag)
    }
    fillTiles('hero-tiles', 12, { names: true, video: true })
    fillTiles('dark-tiles', 9, { names: true, offset: 3 })
    fillTiles('sheet-tiles', 9, { offset: 5 })
    fillTiles('dash-tiles', 10, { offset: 2 })

    // --- Decoratieve QR (geen echte code, puur sfeer) ---------------------
    const drawQR = (id: string) => {
      const svg = $('#' + id)
      if (!svg) return
      const n = 11
      const cell = 100 / n
      let seed = 7
      let out = '<rect width="100" height="100" rx="6" fill="#fff"/>'
      const corner = (x: number, y: number) =>
        `<rect x="${x}" y="${y}" width="${cell * 3}" height="${cell * 3}" rx="2.5" fill="#2a2320"/>` +
        `<rect x="${x + cell * 0.7}" y="${y + cell * 0.7}" width="${cell * 1.6}" height="${cell * 1.6}" rx="1.5" fill="#fff"/>`
      for (let r = 0; r < n; r++) {
        for (let c = 0; c < n; c++) {
          const isCorner = (r < 3 && c < 3) || (r < 3 && c > n - 4) || (r > n - 4 && c < 3)
          if (isCorner) continue
          seed = (seed * 1103515245 + 12345) & 0x7fffffff
          if ((seed >> 8) % 100 < 46) {
            out += `<rect x="${c * cell + 1}" y="${r * cell + 1}" width="${cell - 2}" height="${cell - 2}" rx="1.2" fill="#2a2320"/>`
          }
        }
      }
      out += corner(2, 2) + corner(100 - cell * 3 - 2, 2) + corner(2, 100 - cell * 3 - 2)
      svg.innerHTML = out
    }
    drawQR('qr-svg')
    drawQR('qr-svg-2')

    // --- Confetti-specks ---------------------------------------------------
    const sprinkle = (id: string, count: number) => {
      const el = $('#' + id)
      if (!el) return
      el.textContent = ''
      const cols = ['var(--c1)', 'var(--c2)', 'var(--c3)', 'var(--c4)', 'var(--c5)']
      const frag = document.createDocumentFragment()
      for (let i = 0; i < count; i++) {
        const s = document.createElement('span')
        s.className = 'speck'
        s.style.left = Math.random() * 100 + '%'
        s.style.top = Math.random() * 100 + '%'
        s.style.background = cols[i % cols.length]
        s.style.setProperty('--r', Math.random() * 90 - 45 + 'deg')
        s.style.transform = `rotate(${Math.random() * 90 - 45}deg)`
        s.style.animationDelay = Math.random() * 7 + 's'
        s.style.opacity = (0.5 + Math.random() * 0.4).toFixed(2)
        frag.appendChild(s)
      }
      el.appendChild(frag)
    }
    sprinkle('hero-confetti', 16)
    sprinkle('final-confetti', 22)

    // --- Mobiel menu -------------------------------------------------------
    const burger = $('#nav-burger')
    const menu = $('#mobile-menu')
    const closeMenu = () => {
      if (menu) menu.hidden = true
      if (burger) {
        burger.setAttribute('aria-expanded', 'false')
        burger.setAttribute('aria-label', 'Menu openen')
      }
    }
    if (burger && menu) {
      const onBurger = () => {
        const opening = menu.hidden
        menu.hidden = !opening
        burger.setAttribute('aria-expanded', String(opening))
        burger.setAttribute('aria-label', opening ? 'Menu sluiten' : 'Menu openen')
      }
      burger.addEventListener('click', onBurger)
      cleanups.push(() => burger.removeEventListener('click', onBurger))
      menu.querySelectorAll('a').forEach((a) => {
        a.addEventListener('click', closeMenu)
        cleanups.push(() => a.removeEventListener('click', closeMenu))
      })
    }

    // --- Actieve sectie meelichten (scrollspy) -----------------------------
    const spyLinks: Record<string, HTMLElement[]> = {}
    root.querySelectorAll<HTMLElement>('.nav-links a, .mobile-menu a:not(.btn)').forEach((a) => {
      const href = a.getAttribute('href') || ''
      if (href.charAt(0) !== '#') return
      const id = href.slice(1)
      ;(spyLinks[id] = spyLinks[id] || []).push(a)
    })
    const setActive = (id: string | null) => {
      Object.keys(spyLinks).forEach((k) => {
        spyLinks[k].forEach((a) => a.classList.toggle('active', k === id))
      })
    }
    const spyIds = ['hoe', 'prijzen', 'faq', 'contact']
    const updateSpy = () => {
      const line = window.scrollY + window.innerHeight * 0.35
      let current: string | null = null
      spyIds.forEach((id) => {
        const el = $('#' + id)
        if (el && el.offsetTop <= line) current = id
      })
      setActive(current)
    }
    window.addEventListener('scroll', updateSpy, { passive: true })
    window.addEventListener('resize', updateSpy)
    cleanups.push(() => window.removeEventListener('scroll', updateSpy))
    cleanups.push(() => window.removeEventListener('resize', updateSpy))
    updateSpy()

    // --- Contactformulier --------------------------------------------------
    const cform = $('#contact-form') as HTMLFormElement | null
    if (cform) {
      const onSubmit = (e: Event) => {
        e.preventDefault()
        const naam = $('#c-naam') as HTMLInputElement | null
        const mail = $('#c-mail') as HTMLInputElement | null
        const bericht = $('#c-bericht') as HTMLTextAreaElement | null
        const err = $('#c-error')
        if (!naam || !mail || !bericht) return
        const okMail = /\S+@\S+\.\S+/.test(mail.value)
        if (!naam.value.trim() || !okMail || !bericht.value.trim()) {
          if (err) err.hidden = false
          ;(!naam.value.trim() ? naam : !okMail ? mail : bericht).focus()
          return
        }
        if (err) err.hidden = true
        cform.hidden = true
        const done = $('#form-done')
        if (done) {
          done.hidden = false
          done.focus()
        }
      }
      cform.addEventListener('submit', onSubmit)
      cleanups.push(() => cform.removeEventListener('submit', onSubmit))
    }

    // Mailadres kopiëren (met terugval op selecteren).
    const copyBtn = $('#copy-email')
    const emailEl = $('#contact-email')
    if (copyBtn && emailEl) {
      const onCopy = () => {
        const flash = () => {
          const t = copyBtn.textContent
          copyBtn.textContent = 'Gekopieerd'
          window.setTimeout(() => {
            copyBtn.textContent = t
          }, 1600)
        }
        const selectIt = () => {
          try {
            const range = document.createRange()
            range.selectNodeContents(emailEl)
            const sel = window.getSelection()
            sel?.removeAllRanges()
            sel?.addRange(range)
          } catch {
            /* selecteren lukte niet; dan niet */
          }
        }
        try {
          navigator.clipboard.writeText(emailEl.textContent || '').then(flash, selectIt)
        } catch {
          selectIt()
        }
      }
      copyBtn.addEventListener('click', onCopy)
      cleanups.push(() => copyBtn.removeEventListener('click', onCopy))
    }

    // --- Reveal bij scrollen ----------------------------------------------
    const items = root.querySelectorAll<HTMLElement>('.reveal')
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduce || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('in'))
    } else {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((en) => {
            if (en.isIntersecting) {
              en.target.classList.add('in')
              io.unobserve(en.target)
            }
          })
        },
        { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
      )
      items.forEach((el) => io.observe(el))
      cleanups.push(() => io.disconnect())
    }

    return () => cleanups.forEach((fn) => fn())
  }, [html])

  return <div className="home" ref={ref} dangerouslySetInnerHTML={{ __html: html }} />
}
